<template>
  <TopBanner
    layout="normal"
    currentPage="Privacy Policy"
    heading="Privacy Policy"
    bannerClass="banner-booking"
  />

  <!-- Page wrapper -->
  <section class="py-16 md:py-24 overflow-visible">
    <div class="max-w-7xl mx-auto px-6 lg:px-10 xl:px-14 overflow-visible">
      <!-- items-start helps sticky in grid; overflow-visible avoids clipping -->
      <div class="grid lg:grid-cols-[260px_1fr] gap-10 lg:gap-14 items-start overflow-visible">
        <!-- TOC (desktop only) -->
        <aside
          class="hidden lg:block sticky self-start"
          :style="{ top: stickyTop + 'px' }"
          aria-label="On this page"
        >
          <h3 class="text-slate-900 text-sm font-semibold uppercase tracking-[0.16em] mb-4">
            On this page
          </h3>
          <nav class="text-slate-600 text-sm space-y-2">
            <a class="toc-link" href="#intro">Introduction</a>
            <a class="toc-link" href="#what-collect">What personal information is collected?</a>
            <a class="toc-link" href="#how-used">What will my information be used for?</a>
            <a class="toc-link" href="#share-data">When and with whom do we share your data?</a>
            <a class="toc-link" href="#security">Security Coverage</a>
            <a class="toc-link" href="#links">Links To Other Sites</a>
            <a class="toc-link" href="#changes">Changes To This Privacy Policy</a>
            <a class="toc-link" href="#contact">Contact us</a>
          </nav>
        </aside>

        <!-- Main content -->
        <article class="prose-area">
          <!-- Intro -->
          <section id="intro" class="section-block">
            <p>
              Your privacy is not something that is taken lightly. We take it very seriously. We commit to protecting and safeguarding your personal information
              loaded on our site. Please read the steps mentioned below which describe how your personal information is used and processed. Moreover, you can contact
              us regarding any questions that you may have about your personal information.
            </p>
            <p>
              www.carsirent.com offers online car rental, chauffeur drive, and airport transfer services through its website, mobile application, and other online
              partners' platforms. The details mentioned below apply to all the platforms stated earlier.
            </p>
          </section>

          <!-- What personal info is collected -->
          <section id="what-collect" class="section-block">
            <h3>What personal information is collected?</h3>
            <p>
              When you make a booking or request, you will be required to fill in your information such as the lead drivers' name, age, contact details
              (some are mandatory and others are optional), and payment information. Passport and driving license information will be required at the time of
              pickup of the car.
            </p>
            <p>
              When you make a booking you will automatically have a client account and an email will be sent to you with your login details. This account will
              help you manage your bookings.
            </p>
            <p>
              Visits to our website provide us with certain information, like your IP address, browser info, your computer, application version, and language
              settings. Likewise, if you are using a mobile we might also have access to data, specific settings, and characteristics along with latitude/longitude
              details. We might also access and process data related to the type of applications installed on a mobile device. Though none of the information could
              determine who you are, we still consider it personal information. Using social media would give us access to more of your personal information.
            </p>
          </section>

          <!-- What will info be used for -->
          <section id="how-used" class="section-block">
            <h3>What will my information be used for?</h3>
            <ul class="nice-list">
              <li>Complete your booking and inform the car rental provider of your details at the destination.</li>
              <li>To allow our customer service team to assist you in case of any issues relative to your booking.</li>
              <li>To send information regarding the occasional promotions that you may have chosen.</li>
              <li>
                To contact you for urgent matters such as incomplete bookings, additional information to complete your booking, or last-minute requests that
                need urgent attention. This might require us to contact you by email, phone, etc.
              </li>
            </ul>
          </section>

          <!-- Sharing data -->
          <section id="share-data" class="section-block">
            <h3>When and with whom do we share your data?</h3>
            <ul class="nice-list">
              <li>To complete your bookings, we would need to share your information with the car rental company at your destination.</li>
              <li>Through other portals associated with us and using our engine to process bookings.</li>
              <li>In case of legal issues, we might be required by any legal authorities to disclose your personal information.</li>
              <li>
                <p class="font-semibold text-slate-800">Google Analytics</p>
                <div class="space-y-4 mt-2">
                  <p>
                    Google Analytics is a web analytics service offered by Google that tracks and reports website traffic. Google uses the data collected to
                    track and monitor the use of our Service. This data is shared with other Google services. Google may use the collected data to contextualize
                    and personalize the ads of its own advertising network.
                  </p>
                  <p>
                    You can opt out of having made your activity on the Service available to Google Analytics by installing the Google Analytics opt-out browser
                    add-on. The add-on prevents the Google Analytics JavaScript (ga.js, analytics.js, and dc.js) from sharing information with Google Analytics
                    about visits activity.
                  </p>
                  <p>
                    For more information on the privacy practices of Google, please visit the Google Privacy &amp; Terms web page:
                    <a
                      href="https://policies.google.com/privacy?hl=en"
                      target="_blank"
                      rel="noopener"
                      class="inline-link"
                    >https://policies.google.com/privacy?hl=en</a>
                  </p>
                </div>
              </li>
            </ul>
          </section>

          <!-- Security -->
          <section id="security" class="section-block">
            <h3>Security Coverage</h3>
            <p>All our systems are secured and no information will be sent to a third party besides those that are mentioned above.</p>
          </section>

          <!-- Links -->
          <section id="links" class="section-block">
            <h3>Links To Other Sites</h3>
            <p>
              Our Service may contain links to other sites that are not operated by us. If you click on a third-party link, you will be directed to that
              third-partys' site. We strongly advise you to review the Privacy Policy of every site you visit.
            </p>
            <p>We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services.</p>
          </section>

          <!-- Changes -->
          <section id="changes" class="section-block">
            <h3>Changes To This Privacy Policy</h3>
            <p>We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.</p>
            <p>You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when posted.</p>
          </section>

          <!-- Contact -->
          <section id="contact" class="section-block">
            <h3>Contact us</h3>
            <p>
              In case of further clarifications or comments, contact us at
              <a href="mailto:bookings@vehitraworld.com" class="inline-link">bookings@vehitraworld.com</a> or call
              <a href="tel:+97317001550" class="inline-link">+973 17001550</a>.
            </p>
          </section>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import TopBanner from '@/components/TopBanner.vue'

const stickyTop = ref(104) // default fallback

onMounted(() => {
  // Try to detect your site header’s height so TOC clears it exactly
  const header = document.querySelector('header')
  if (header) {
    const h = header.getBoundingClientRect().height
    // Add a small breathing space (8–12px)
    stickyTop.value = Math.round(h + 12)
  }
})
</script>


<style scoped>
/* Base prose area */
.prose-area {
  @apply text-slate-900; /* darker body text base */
}

/* Paragraphs */
.prose-area p {
  @apply text-[15px] leading-7 md:text-[16px] md:leading-8 text-slate-800 font-normal; /* solid readability */
}

/* Headings */
.prose-area h3 {
  @apply text-slate-900 text-xl md:text-2xl font-semibold;
}

/* Section blocks */
.section-block {
  @apply space-y-4 md:space-y-5;
  scroll-margin-top: 120px; /* offset for header */
}
.section-block + .section-block {
  @apply pt-8 md:pt-10 border-t border-slate-200; /* subtle divider */
}

/* Lists */
.nice-list {
  @apply mt-4 space-y-3 pl-5;
  list-style-type: square;
}
.nice-list li {
  @apply text-[15px] leading-7 md:text-[16px] md:leading-8 text-slate-800 font-normal;
}

/* TOC (sidebar navigation links) */
.toc-link {
  @apply block px-3 py-2 rounded-lg hover:bg-slate-100 text-slate-700 hover:text-slate-900 transition;
  font-size: 14px;
}

/* Inline hyperlinks */
.inline-link {
  @apply text-emerald-700 hover:text-emerald-800 hover:underline font-medium;
}
</style>